<template>
  <article class="rounded-[32px] text-dark relative grid grid-cols-1 lg:grid-cols-3 gap-4">
    <div class="min-h-[220px] overflow-hidden rounded-t-lg lg:rounded-none lg:rounded-l-xl bg-white/10">
      <img
        v-if="imageUrl"
        :src="imageUrl"
        :alt="offer.title || 'Special offer'"
        class="h-full min-h-[220px] w-full object-cover"
        loading="lazy"
      >
    </div>
    <div class="col-span-1 lg:col-span-2 p-6 lg:py-10 lg:pl-0">
      <div class="inline-flex rounded-full text-sm font-semibold uppercase tracking-[0.12em]"
           :class="`text-${attraction.page_style}-primary-tone`">
        {{ offer.tagline }}
      </div>

      <h3 class="mt-4 text-2xl font-semibold leading-tight" :class="textClass">
        {{ offer.title }}
      </h3>

      <p v-if="offer.description" class="mt-4 leading-7" :class="textClass">
        {{ offer.description }}
      </p>

      <div class="mt-6 flex items-center justify-between gap-4">
        <a
            v-if="href"
            :href="href"
            @click.prevent="handleUpsellClick"
            class="inline-flex items-center justify-center rounded-full px-6 py-3 text-[15px] font-semibold text-dark transition hover:opacity-90"
            :class="`bg-${attraction.page_style}-primary`"
        >
          {{ buttonLabel }}
        </a>

        <div
            v-else
            class="rounded-full bg-white px-6 py-3 text-[15px] font-semibold text-dark/60"
        >
          Offer available soon
        </div>
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue'
import useStore from '../store'
import { formatUpsellDiscount, resolveUpsellAttraction, resolveUpsellButtonLabel, resolveUpsellHref } from '../helpers/upsells'
import { trackEvent } from '../../helpers/track'
import Twist from "@/checkout/components/Twist.vue";

const props = defineProps({
  offer: {
    type: Object,
    required: true,
  },
  badge: {
    type: String,
    default: 'Special Offer',
  },
  textClass: {
    type: String,
    default: 'text-light',
  },
})

const store = useStore()
const attraction = computed(() => resolveUpsellAttraction(props.offer))
const href = computed(() => resolveUpsellHref(props.offer))
const discountLabel = computed(() => formatUpsellDiscount(props.offer))
const isSelected = computed(() => store.isUpsellSelected(props.offer?.id))
const imageUrl = computed(() => {
  const imagePath = String(props.offer?.image_path ?? '').replace(/^\/+/, '')

  if (!imagePath) {
    return ''
  }

  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath
  }

  const pathParts = imagePath.split('/').filter(Boolean)
  const filename = pathParts.pop()

  if (!filename) {
    return ''
  }

  const configuredBase = String(window.bookingWidget?.base ?? '').replace(/\/$/, '')
  const base = configuredBase
    ? new URL(configuredBase, window.location.origin).origin
    : ''
  const directory = pathParts.length ? `${pathParts.join('/')}/` : ''

  return `${base}/image-transform/${directory}width=800/${filename}`
})
const buttonLabel = computed(() => isSelected.value
  ? 'Add tickets'
  : resolveUpsellButtonLabel(props.offer)
)

const trackUpsellClick = () => {
  if (!attraction.value?.code) {
    return
  }

  trackEvent(`booking-upsell-select-${attraction.value.code}`)
}

const handleUpsellClick = () => {
  store.selectUpsell(props.offer)
  trackUpsellClick()

  if (href.value) {
    window.location.assign(href.value)
  }
}
</script>
